import { useVirtualizer } from '@tanstack/react-virtual';
import type { ChipDto } from '@pietrochip/shared';
import { useRef } from 'react';
import { personName, time } from '../lib/format';
import { StateMark } from './ui';

/**
 * Elenco completo dei chip, con la spunta su ognuno.
 *
 * La lista è virtualizzata: di un evento si vedono qualche decina di righe per
 * volta, ma i chip possono essere duemila, e renderizzarli tutti renderebbe
 * lento ogni tasto premuto nella barra di ricerca — che è l'unica cosa che deve
 * restare istantanea. Si disegna solo ciò che sta nello schermo.
 *
 * Le righe sono alte in modo variabile (il nome può andare a capo), quindi
 * l'altezza la misura il DOM invece di essere stimata: con una stima fissa la
 * barra di scorrimento mentirebbe sulle liste lunghe.
 */
export function ChipList({
  chips,
  totalCount,
  busyChipId,
  onReturn,
  onUnreturn,
  onRemove,
}: {
  chips: readonly ChipDto[];
  /** Chip dell'evento in tutto, prima dei filtri. */
  totalCount: number;
  busyChipId: string | null;
  onReturn: (chip: ChipDto) => void;
  onUnreturn: (chip: ChipDto) => void;
  onRemove: (chip: ChipDto) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: chips.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 76,
    overscan: 8,
  });

  const items = virtualizer.getVirtualItems();

  return (
    <div className="chip-list">
      <div className="chip-list__head">
        <span>
          {chips.length === totalCount
            ? `${totalCount} chip`
            : `${chips.length} di ${totalCount} chip`}
        </span>
      </div>

      {chips.length === 0 ? (
        <p className="muted" style={{ padding: '18px 14px' }}>
          Nessun chip corrisponde ai filtri.
        </p>
      ) : (
        <div className="chip-list__viewport" ref={scrollRef}>
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
            {items.map((item) => {
              const chip = chips[item.index];
              if (!chip) return null;

              return (
                <div
                  key={chip.id}
                  data-index={item.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${item.start}px)`,
                  }}
                >
                  <ChipRow
                    chip={chip}
                    busy={busyChipId === chip.id}
                    onReturn={() => onReturn(chip)}
                    onUnreturn={() => onUnreturn(chip)}
                    onRemove={() => onRemove(chip)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ChipRow({
  chip,
  busy,
  onReturn,
  onUnreturn,
  onRemove,
}: {
  chip: ChipDto;
  busy: boolean;
  onReturn: () => void;
  onUnreturn: () => void;
  onRemove: () => void;
}) {
  const returned = chip.returnedAt !== null;
  const who = personName(chip.firstName, chip.lastName);

  const meta = [
    chip.race ?? null,
    chip.raceNumber ? `n. ${chip.raceNumber}` : null,
    chip.category,
    chip.team,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="chip-row">
      <div style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 10 }}>
          <span className="mono chip-row__code">{chip.code}</span>
          <StateMark returned={returned} withLabel={false} />
        </div>
        <div className="chip-row__who">
          {who}
          {chip.addedManually && <span className="faint"> · aggiunto a mano</span>}
        </div>
        {meta && <div className="chip-row__meta truncate">{meta}</div>}
      </div>

      <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
        {returned ? (
          <>
            <span className="chip-row__meta" title={chip.returnedAt ?? undefined}>
              {time(chip.returnedAt)}
              {chip.returnedByName ? ` · ${chip.returnedByName}` : ''}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              disabled={busy}
              onClick={onUnreturn}
              aria-label={`Annulla la restituzione di ${chip.code}`}
            >
              {busy ? '…' : 'Annulla'}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn--return"
            onClick={onReturn}
            aria-label={`Segna restituito il chip ${chip.code}`}
          >
            Restituito
          </button>
        )}

        <button
          type="button"
          className="btn btn--sm btn--quiet"
          disabled={busy}
          onClick={onRemove}
          aria-label={`Rimuovi il chip ${chip.code} dall’elenco`}
          title="Rimuovi dall’elenco (recuperabile)"
        >
          Rimuovi
        </button>
      </div>
    </div>
  );
}
